import { makeAutoObservable } from 'mobx';
import _ from 'lodash';
import {DomainStore, fetchModel} from 'shared/store';
import {api} from 'shared/core';
import {endpoints, types, setupAutosaveModel} from 'main/tools';
import {Shift, Timesheet} from 'main/stores/time_tracking';
import moment from 'moment';

class TimesheetEditorState {
  store = new DomainStore();
  employeeSelectorState;

  timesheet;
  errors = {};
  editingEntry = {};
  viewingEntry = {};
  viewingClockEvent = {};
  payPeriods = [];
  payPeriod;
  employee;

  isLoadingEntries = false;

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  async receiveProps({payPeriods, employee, payPeriod, onPayPeriodSelected, employeeSelectorState, myTimesheet}) {
    this.employeeSelectorState = employeeSelectorState;
    this.payPeriods = payPeriods;
    this.onPayPeriodSelected = onPayPeriodSelected;
    this.myTimesheet = myTimesheet;
    const currentEmployee = this.employee;
    const currentPayPeriod = this.payPeriod;
    this.employee = employee;
    this.payPeriod = payPeriod;

    if (currentEmployee && (_.get(this.employee, 'id') !== _.get(currentEmployee, 'id') || this.payPeriod.id !== _.get(currentPayPeriod, 'id'))) {
      this.isLoadingEntries = true;
      await Promise.all([this.loadEntries()]);
      if (this.isShowing(employee, payPeriod)) this.isLoadingEntries = false;
    }
  }

  isShowing(employee, payPeriod) {
    return this.employee.id === employee.id && this.payPeriod.id === payPeriod.id;
  }

  async load() {
    await this.store._compose([
      endpoints.TIME_OFF.TYPES.ALL
    ]);

    this.types = this.store._getAll(types.TIME_OFF.TYPE);

    await Promise.all([this.loadEntries()]);
  }

  async loadEntries() {
    const {employee, payPeriod} = this;
    this.errors = {};

    const timesheet = new Timesheet(await fetchModel(
      endpoints.TIME_TRACKING.ENTRIES_FOR_PAY_PERIOD.with(payPeriod.id, employee.id),
      types.TIME_TRACKING.TIMESHEET
    ));
    const shifts = await this.fetchShifts(timesheet, employee, payPeriod);
    if (!this.isShowing(employee, payPeriod)) return;

    this.timesheet = timesheet;
    this.timesheet.shifts = shifts;

    this.timesheet.entries.forEach(entry => {
      setupAutosaveModel(entry, returnedEntry => this.updateTimesheetFromEntry(returnedEntry, entry));
    });

    this.timesheet.fillMissingDates(payPeriod);
    this.timesheet.addEntriesForUnmatchedShifts();
  }

  async fetchShifts(timesheet, employee, payPeriod) {
    if (!timesheet.schedulingEnabled) return [];

    const shiftStore = new DomainStore();
    await shiftStore._compose([{
      url: endpoints.TIME_TRACKING.SHIFTS.ALL,
      params: {
        'filter[employee_id]': employee.id,
        'filter[published]': true,
        start_date: moment(payPeriod.startDate).format('YYYY-MM-DD'),
        end_date: moment(payPeriod.endDate).format('YYYY-MM-DD')
      }
    }]);

    return shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift);
  }

  canApplyShift(entry) {
    return !!this.timesheet.shiftFor(entry) && entry.noValues && !this.cannotCreateOrEdit(entry);
  }

  async applyShift(entry) {
    this.timesheet.applyShift(entry);
    await this.submitChanges(entry);
  }

  updateTimesheetFromEntry(returnedEntry, entryInMemory) {
    this.timesheet.updateTotals(returnedEntry.totals);
    if (this.employeeSelectorState && this.timesheet.approved !== returnedEntry.timesheetApproved) {
      this.employeeSelectorState.interactiveAgent.refresh();
    }
    this.timesheet.approved = returnedEntry.timesheetApproved;
    if (returnedEntry.deletedAt || (entryInMemory.endTime && entryInMemory.endTime < moment(this.payPeriod.startDate).utc(true))) {
      return this.timesheet.removeEntry(entryInMemory, this.payPeriod);
    }
    entryInMemory.markSaved();
  }

  async updateEntry(entry, values) {
    entry.merge(values);
    await this.submitChanges(entry);
  }

  async updateEntryDimensionValue(entry, dimension, value) {
    entry.setDimensionValue(dimension, value);
    await this.submitChanges(entry);
  }

  async submitChanges(entry) {
    if (entry.isCreating) { return; }

    entry.setSubmitValues(this.payPeriod);

    if (entry.isNew) {
      entry.isCreating = true;
      const {model, errors} = await this.store.post(
        endpoints.TIME_TRACKING.ENTRIES.with(this.employee.id),
        types.TIME_TRACKING.ENTRY,
        entry
      );

      entry.errors = errors;
      if (model) {
        entry.merge(_.pick(model, ['id', 'links', '_type']));
        setupAutosaveModel(entry, returnedEntry => this.updateTimesheetFromEntry(returnedEntry, entry));
        entry.setSubmitValues(this.payPeriod);
        entry.autosaver.autosaveImmediately();
      }
      entry.isCreating = false;
    } else {
      await entry.autosaver.autosave();
    }
  }

  saveEntryNote() {
    this.updateEntry(this.timesheet.entryFor(this.editingEntry.uuid), {note: this.editingEntry.note});
    this.closeEditEntryNoteModal();
  }

  saveEntryBreak() {
    this.updateEntry(this.timesheet.entryFor(this.editingEntry.uuid), {unpaidBreakMinutes: this.editingEntry.unpaidBreakMinutes});
    this.closeEditEntryBreakModal();
  }

  async approve() {
    await this.timesheet.flushPendingEntryAutosaves();
    const {model, errors} = await this.store.post(
      endpoints.TIME_TRACKING.APPROVE.with(this.payPeriod.id, this.employee.id),
      types.TIME_TRACKING.TIMESHEET
    );

    this.errors = errors;
    if (model) {
      this.timesheet.mergeApproval(model);
      if (this.employeeSelectorState) this.employeeSelectorState.interactiveAgent.refresh();
    }
  }

  async deleteEntry(entry) {
    if (!entry.isNew) {
      const {data} = await api.post(`${entry.link('destroyEntry')}?pay_period_id=${this.payPeriod.id}`);
      entry.merge(data.data.attributes);
      this.updateTimesheetFromEntry(data.data.attributes, entry);
    } else {
      this.timesheet.removeEntry(entry, this.payPeriod);
    }
  }

  get canCreateEntries() {
    if (this.myTimesheet && this.timesheet.geofencingEnabled) return false;

    return this.timesheet.canCreateEntries;
  }

  customLinksFor(entry) {
    if (entry.noValues) {
      if (!this.timesheet.shiftFor(entry) && this.timesheet.entriesOn(entry.date).length > 1) {
        return [
          {
            text: 'time_tracking.timesheet.Delete',
            action: entry => this.deleteEntry(entry)
          },
        ];
      } else {
        return [];
      }
    }

    const isNotEntry = entry._type === types.TIME_OFF.ABSENCE || entry._type === types.HOLIDAY;
    if (isNotEntry || !entry.canPatch) return [];

    const links = [
      {
        text: `time_tracking.timesheet.${entry.hasNote ? 'Edit Note' : 'Add Note'}`,
        action: entry => this.openEditEntryNoteModal(entry)
      }
    ];

    if (entry.hasLink('destroyEntry')) {
      links.push({
        text: 'time_tracking.timesheet.Delete',
        action: entry => this.deleteEntry(entry)
      });
    }

    if (!this.timesheet.manuallyEnterHours && entry.manuallyEnteredHours === undefined) {
      links.push({
        text: `time_tracking.timesheet.${entry.hasBreak ? 'Edit Unpaid Break' : 'Add Unpaid Break'}`,
        action: entry => this.openEditEntryBreakModal(entry)
      });
    }

    if (this.canCreateEntries && entry.date >= this.payPeriod.startDate) {
      links.push({
        text: 'time_tracking.timesheet.Add New Entry',
        action: entry => this.timesheet.addEntry(entry.date)
      });
    }

    return links;
  }

  cannotCreateOrEdit(entry) {
    return !this.canCreateEntries || (!entry.isNew && !entry.canPatch);
  }

  typeNameById(id) {
    return _.find(this.types, {id}).name;
  }
}

export default TimesheetEditorState;
